import React, { useLayoutEffect, useMemo } from "react";
import { Helmet } from "react-helmet-async";
import { useLocation } from "react-router-dom";
import {
  getDefaultSiteOrganizationJsonLd,
  normalizeCanonicalUrl,
} from "../../utils/structuredData";
import { applyDocumentHeadSeo } from "../../utils/documentHeadSeo";
import { finalizeSeoTitle } from "../../../shared/utils/seoTitle";
import { ensureMetaDescriptionLength } from "../../../shared/seo/seoDescription";
import { parsePublicSeoDetailPath } from "../../../shared/seo/publicSeoDetailPath";
import { ssrSeoForCurrentPath } from "../../utils/ssrSeoBootstrap";
import { useResolvedSeo } from "../../hooks/useResolvedSeo";
import {
  APT_OG_IMAGE_URL,
  resolveSocialCard,
} from "../../../shared/seo/socialCard";
import HelmetRenderErrorBoundary from "./HelmetRenderErrorBoundary";

interface SEOHeadProps {
  title?: string;
  description?: string;
  keywords?: string;
  canonical?: string;
  /** Pagination / series (listing hubs) */
  linkPrev?: string;
  linkNext?: string;
  /** Entity image *candidate* — resolver may fall back to the APT platform card. */
  ogImage?: string;
  ogType?: string;
  structuredData?: object | object[];
  noIndex?: boolean;
  language?: string;
  alternateLanguages?: Array<{
    hreflang: string;
    href: string;
  }>;
  preloadImages?: string[]; // Critical images for LCP optimization
  /** Google-supported robots directive string. When omitted, derived from noIndex (index,follow vs noindex,nofollow). */
  robotsContent?: string;
  /** Google-supported googlebot directive; defaults to the same value as robots meta when omitted. */
  googlebotContent?: string;
  /** Search Console HTML tag verification token; also reads VITE_GOOGLE_SITE_VERIFICATION when unset. */
  googleSiteVerification?: string;
  /**
   * Course detail still has the better head in the browser. Skip the SSR bootstrap
   * and the resolved-head fetch until that server builder matches the client.
   */
  preferClientSeo?: boolean;
}

function stricterRobots(clientRobots: string, serverRobots: string | undefined): string {
  if (!serverRobots) return clientRobots;
  const clientNoindex = clientRobots.toLowerCase().includes("noindex");
  const serverNoindex = serverRobots.toLowerCase().includes("noindex");
  if (clientNoindex || serverNoindex) {
    const nofollow =
      clientRobots.toLowerCase().includes("nofollow") ||
      serverRobots.toLowerCase().includes("nofollow");
    return nofollow ? "noindex, nofollow" : "noindex, follow";
  }
  return serverRobots;
}

const DEFAULT_DESCRIPTION =
  "American Police Training helps law enforcement professionals find training events, courses, and instructors across the U.S. to advance public safety careers.";
const DEFAULT_TITLE =
  "American Police Training - Law Enforcement Training Events & Courses";

/** Single source of truth for fallback OG image. Used by SEOHead and index.html. */
export const DEFAULT_OG_IMAGE_URL = APT_OG_IMAGE_URL;

const SEOHead: React.FC<SEOHeadProps> = ({
  title = DEFAULT_TITLE,
  description = DEFAULT_DESCRIPTION,
  keywords = "police training, law enforcement, training events, certification, law enforcement courses, police education, tactical training, law enforcement instructors",
  canonical,
  linkPrev,
  linkNext,
  ogImage,
  ogType = "website",
  structuredData,
  noIndex = false,
  language = "en",
  alternateLanguages = [],
  preloadImages = [],
  robotsContent,
  googlebotContent,
  googleSiteVerification,
  preferClientSeo = false,
}) => {
  const location = useLocation();
  const pathname = location.pathname;
  const ssr = preferClientSeo ? null : ssrSeoForCurrentPath(pathname);
  const detailPath = parsePublicSeoDetailPath(pathname);
  const shouldFetchResolved = !preferClientSeo && !ssr && detailPath != null;
  const resolvedSeo = useResolvedSeo(pathname, shouldFetchResolved);
  const holdForResolved =
    shouldFetchResolved &&
    resolvedSeo.status !== "ready" &&
    resolvedSeo.status !== "error";
  const resolved =
    resolvedSeo.status === "ready" && resolvedSeo.head ? resolvedSeo.head : null;

  const effectiveTitle =
    title != null && String(title).trim() !== "" ? title : DEFAULT_TITLE;
  const effectiveDescription =
    description != null && String(description).trim() !== ""
      ? description
      : DEFAULT_DESCRIPTION;
  const clientTitle = finalizeSeoTitle(String(effectiveTitle).trim());
  const clientDescription = ensureMetaDescriptionLength(effectiveDescription);
  const locked = ssr ?? resolved;
  const fullTitle = locked ? locked.title : clientTitle;
  const optimizedDescription = locked ? locked.description : clientDescription;
  const clientCanonical = canonical
    ? normalizeCanonicalUrl(canonical)
    : normalizeCanonicalUrl(
        typeof window !== "undefined" ? window.location.href : "https://americanpolicetraining.com/",
      );
  const fullCanonical = locked?.canonical
    ? normalizeCanonicalUrl(locked.canonical)
    : clientCanonical;

  const socialCard = resolveSocialCard({
    path: fullCanonical,
    entityImageUrl: ogImage,
  });
  const effectiveOgImage = socialCard.ogImage;
  const serverOgUrl =
    typeof document !== "undefined"
      ? document.head
          .querySelector('meta[property="og:url"]')
          ?.getAttribute("content")
          ?.trim() || ""
      : "";
  const serverSocialLocked =
    typeof document !== "undefined" &&
    Boolean(
      document.head.querySelector(
        'meta[property="og:image"][data-apt-social="1"]',
      ),
    ) &&
    serverOgUrl !== "" &&
    normalizeCanonicalUrl(serverOgUrl) === fullCanonical;

  const defaultRobotsDirective = noIndex
    ? "noindex, nofollow"
    : "index, follow";
  const clientRobots =
    robotsContent != null && String(robotsContent).trim() !== ""
      ? String(robotsContent).trim()
      : defaultRobotsDirective;
  const robotsMeta = locked ? stricterRobots(clientRobots, locked.robots) : clientRobots;
  const googlebotMeta =
    googlebotContent != null && String(googlebotContent).trim() !== ""
      ? String(googlebotContent).trim()
      : robotsMeta;

  const rawViteGoogleVerify = import.meta.env?.VITE_GOOGLE_SITE_VERIFICATION;
  const envSiteVerification =
    rawViteGoogleVerify != null && String(rawViteGoogleVerify).trim() !== ""
      ? String(rawViteGoogleVerify).trim()
      : "";
  const siteVerificationToken =
    googleSiteVerification != null &&
    String(googleSiteVerification).trim() !== ""
      ? String(googleSiteVerification).trim()
      : envSiteVerification;

  const defaultStructuredData = getDefaultSiteOrganizationJsonLd();

  const structuredDataArray = structuredData
    ? Array.isArray(structuredData)
      ? structuredData
      : [structuredData]
    : [];

  const allStructuredData = [defaultStructuredData, ...structuredDataArray];

  const normalizedPrev = linkPrev ? normalizeCanonicalUrl(linkPrev) : null;
  const normalizedNext = linkNext ? normalizeCanonicalUrl(linkNext) : null;

  const headSeoPayload = useMemo(
    () => ({
      title: fullTitle,
      description: optimizedDescription,
      canonicalHref: fullCanonical,
      keywords,
      author: "American Police Training",
      robots: robotsMeta,
      googlebot: googlebotMeta,
      ogType,
      ogTitle: fullTitle,
      ogDescription: optimizedDescription,
      ogImage: effectiveOgImage,
      ogImageWidth: socialCard.width ?? null,
      ogImageHeight: socialCard.height ?? null,
      skipSocialImage: serverSocialLocked,
      ogSiteName: "American Police Training",
      ogLocale: "en_US",
      twitterCard: socialCard.twitterCard,
      twitterTitle: fullTitle,
      twitterDescription: optimizedDescription,
      twitterImage: effectiveOgImage,
      twitterSite: "@AmericanPoliceTraining",
      linkPrev: normalizedPrev,
      linkNext: normalizedNext,
      googleSiteVerification: siteVerificationToken || null,
    }),
    [
      fullTitle,
      optimizedDescription,
      fullCanonical,
      keywords,
      robotsMeta,
      googlebotMeta,
      ogType,
      effectiveOgImage,
      socialCard.twitterCard,
      socialCard.width,
      socialCard.height,
      serverSocialLocked,
      normalizedPrev,
      normalizedNext,
      siteVerificationToken,
    ],
  );

  useLayoutEffect(() => {
    if (holdForResolved) return;
    applyDocumentHeadSeo(headSeoPayload);
  }, [headSeoPayload, holdForResolved]);

  return (
    <HelmetRenderErrorBoundary>
      <Helmet>
        {/* Core title / description / canonical / OG / Twitter: applied imperatively (applyDocumentHeadSeo) to avoid duplicates vs index.html */}

        <html lang={language} />
        {alternateLanguages.map((lang) => (
          <link
            key={lang.hreflang}
            rel="alternate"
            hrefLang={lang.hreflang}
            href={lang.href}
          />
        ))}

        {/* theme-color / msapplication-TileColor: single node in index.html (avoids duplicate vs shell) */}
        <meta name="mobile-web-app-capable" content="yes" />
        <meta name="apple-mobile-web-app-capable" content="yes" />
        <meta name="apple-mobile-web-app-status-bar-style" content="default" />
        <meta
          name="apple-mobile-web-app-title"
          content="American Police Training"
        />

        {preloadImages.map((imageUrl, index) => (
          <link
            key={`preload-${index}`}
            rel="preload"
            as="image"
            href={imageUrl}
            fetchPriority="high"
            type={
              imageUrl.includes(".webp")
                ? "image/webp"
                : imageUrl.includes(".avif")
                  ? "image/avif"
                  : undefined
            }
          />
        ))}

        {socialCard.preloadOgImage && (
          <link
            rel="preload"
            as="image"
            href={effectiveOgImage}
            fetchPriority="high"
            type={
              effectiveOgImage.includes(".webp")
                ? "image/webp"
                : effectiveOgImage.includes(".avif")
                  ? "image/avif"
                  : undefined
            }
          />
        )}

        {allStructuredData.map((data, index) => (
          <script key={index} type="application/ld+json">
            {JSON.stringify(data)}
          </script>
        ))}
      </Helmet>
    </HelmetRenderErrorBoundary>
  );
};

export default SEOHead;
